<script setup lang="ts">
  /**
   * CheckboxField.vue
   *
   * A customizable checkbox component that integrates with vee-validate for form handling and validation.
   *
   * Props:
   * - name: string (required) - The name of the checkbox field.
   * - label: string (optional) - The label text for the checkbox.
   * - modelValue: boolean | string | number (optional, default: false) - The initial value of the checkbox.
   * - rules: RuleExpression<any> | undefined (optional) - Validation rules for the checkbox.
   * - checkedValue: boolean | string | number (optional, default: true) - The value when the checkbox is checked.
   * - uncheckedValue: boolean | string | number (optional, default: false) - The value when the checkbox is unchecked.
   * - indeterminate: boolean (optional, default: false) - Sets the native checkbox indeterminate state.
   * - ariaLabel: string (optional) - Accessible name for the input when no visible label is used.
   *
   * Usage Examples:
   *
   * 1. Basic usage with default true/false values:
   *    <CheckboxField
   *      name="acceptTerms"
   *      label="I accept the terms and conditions"
   *      :rules="yup.boolean().oneOf([true], 'You must agree to the terms')"
   *    />
   *
   * 2. Custom checked/unchecked values:
   *    <CheckboxField
   *      name="subscription"
   *      label="Subscribe to newsletter"
   *      checkedValue="subscribed"
   *      uncheckedValue="unsubscribed"
   *      :rules="yup.string().oneOf(['subscribed'], 'You must be subscribed')"
   *    />
   *
   * 3. Using v-model for two-way binding:
   *    <CheckboxField
   *      v-model="formData.acceptMarketing"
   *      name="acceptMarketing"
   *      label="I agree to receive marketing emails"
   *    />
   *
   * Notes:
   * - The component uses vee-validate's useField for form integration and validation.
   * - If checkedValue and uncheckedValue are not set, it defaults to true/false respectively.
   * - The component supports both boolean and string/number values for checked and unchecked states.
   * - Validation rules can be passed as a Yup schema or as a vee-validate compatible rule object.
   * - The component includes error display and integrates with vee-validate's form handling.
   */

  import { type RuleExpression, useField } from 'vee-validate'
  import { ExclamationCircleIcon } from '@heroicons/vue/24/outline'
  import {
    computed,
    defineProps,
    type PropType,
    defineEmits,
    nextTick,
    onMounted,
    ref,
    watch
  } from 'vue'
  import { useThemeColor } from '@/composables/useThemeColorV3'

  const props = defineProps({
    name: {
      type: String,
      required: true
    },
    label: {
      type: String,
      required: false
    },
    modelValue: {
      type: [Boolean, String, Number],
      required: false,
      default: false
    },
    rules: {
      type: Object as PropType<RuleExpression<any> | undefined>,
      required: false
    },
    checkedValue: {
      type: [Boolean, String, Number],
      required: false,
      default: true
    },
    uncheckedValue: {
      type: [Boolean, String, Number],
      required: false,
      default: false
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    dataCy: {
      type: String,
      required: false
    },
    error: {
      type: String,
      required: false
    },
    indeterminate: {
      type: Boolean,
      required: false,
      default: false
    },
    ariaLabel: {
      type: String,
      required: false
    }
  })

  const emit = defineEmits(['update:modelValue'])

  const checkboxInputRef = ref<HTMLInputElement | null>(null)

  const syncIndeterminate = () => {
    nextTick(() => {
      const el = checkboxInputRef.value
      if (el) {
        el.indeterminate = props.indeterminate
      }
    })
  }

  watch(
    () => props.indeterminate,
    () => {
      syncIndeterminate()
    },
    { flush: 'post' }
  )

  watch(
    () => props.modelValue,
    () => {
      syncIndeterminate()
    },
    { flush: 'post' }
  )

  const { value, errorMessage, handleBlur, meta, errors } = useField(props.name, props.rules, {
    initialValue: props.modelValue
  })

  const themeColor = computed(() => useThemeColor().value)

  const errorId = computed(() => `${props.name}-error`)
  const displayedError = computed(() => props.error ?? errorMessage.value)
  const ariaDescribedBy = computed(() => {
    return displayedError.value ? errorId.value : undefined
  })

  // Ensure the field value stays in sync with the prop
  watch(
    () => props.modelValue,
    (newVal) => {
      if (value.value !== newVal) {
        value.value = newVal
      }
    },
    { immediate: true }
  )

  const isChecked = computed({
    get: () => {
      // Always use the prop value directly for the checked state
      return props.modelValue === props.checkedValue
    },
    set: (newValue: boolean) => {
      const newModelValue = newValue ? props.checkedValue : props.uncheckedValue
      value.value = newModelValue
      emit('update:modelValue', newModelValue)
    }
  })

  const handleChange = (event: Event) => {
    const target = event.target as HTMLInputElement
    // This will trigger the emit('update:modelValue') in the setter
    isChecked.value = target.checked

    // Also update the vee-validate field value
    value.value = target.checked ? props.checkedValue : props.uncheckedValue
  }

  const handleLabelClick = (event: Event) => {
    // Prevent the default label behavior (which would toggle the checkbox)
    event.preventDefault()

    if (!props.disabled) {
      // Toggle the checkbox value
      const newCheckedState = !isChecked.value
      isChecked.value = newCheckedState

      // Also update the vee-validate field value
      value.value = newCheckedState ? props.checkedValue : props.uncheckedValue
    }
  }

  onMounted(() => {
    // this is needed only initially when this component is loaded
    // used to sync up with the 'update:modelValue' emission
    if (props.modelValue === props.checkedValue) {
      isChecked.value = true
    }
    syncIndeterminate()
  })
</script>

<template>
  <div>
    <div class="flex items-center">
      <input
        :id="props.name"
        ref="checkboxInputRef"
        :class="[
          `h-5 w-5 rounded border-gray-300 focus:ring-${themeColor}-500`,
          disabled
            ? isChecked
              ? 'cursor-not-allowed text-gray-400'
              : 'cursor-not-allowed bg-gray-100 text-gray-400'
            : `cursor-pointer text-${themeColor}-600`
        ]"
        :checked="isChecked"
        type="checkbox"
        :name="props.name"
        :disabled="disabled"
        :data-cy="dataCy"
        :aria-invalid="!!displayedError || undefined"
        :aria-describedby="ariaDescribedBy"
        :aria-label="ariaLabel || undefined"
        @change="handleChange"
        @blur="handleBlur"
      />
      <label
        v-if="label"
        class="ml-2 block select-none text-sm font-medium"
        :class="{ 'opacity-50': disabled }"
        :for="props.name"
        @click="handleLabelClick"
      >
        {{ label }}
      </label>
    </div>
    <div
      v-if="displayedError"
      :id="errorId"
      class="mt-1 flex items-center"
      :class="{ 'opacity-50': disabled }"
      aria-live="polite"
    >
      <ExclamationCircleIcon
        class="mr-1 h-5 w-5 text-red-500"
        aria-hidden="true"
      />
      <p class="text-xs text-red-600">
        {{ displayedError }}
      </p>
    </div>
  </div>
</template>
